import { useEffect, useState, useCallback, useMemo, useContext } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { LoadingDots } from "../../../components/general";
import { SearchComponent } from "../../../components/components/js/SearchComponent";
import { TableComponent } from "../../../components/components/js/TableComponent";
import { HeaderComponent } from "../../../components/components/js/HeaderComponent";
import AddstudentModal from "../../../components/components/js/AddModal";

import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import useAuth from "../../../hooks/useAuth";
import AuthContext from "../../../context/AuthProvider";

import API_URLS from "../../../constants/apiConstants";
import { ADMIN_STUDENTS_ROUTE } from "../../../routes/RoutesPathes";
import { downloadCSV, getFilters } from "../../../utils/exportUtils";

import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "react-i18next";
import QRModal from "../../../components/components/js/QRModal";
import { GeneralDropdown } from "../../../components/components/js/GeneralDropdown";
import Pagination from "../../../components/general/Pagination";

const COLUMNS = [
  "name",
  "age",
  "rooms",
  "parents",
  "status",
  "phone",
  "actions",
];

const SEARCH_FILTERS = (t) => [
  { value: "name", label: t("search.name") },
  { value: "parent", label: t("search.parent") },
  { value: "phone", label: t("search.phone") },
  { value: "email", label: t("search.email") },
];

const formatStudentData = (data) => {
  return data.map((student) => ({
    id: student.id,
    name: student.first_name + " " + (student.last_name || ""),
    image: student.profile_img || "",
    age: student.age || "-",
    rooms: student.class_names || "-",
    parents: student.parents_names || "-",
    phone: student.parents_phones || "-",
    status: student.status || "-",
  }));
};
const STATUS_OPTIONS = (t) => [
  { value: "active", label: t("students.status.active") },
  { value: "inactive", label: t("students.status.inactive") },
  { value: "graduated", label: t("students.status.graduated") },
];
const getStudentFilters = (t, rooms) => [
  {
    key: "room",
    options: [...rooms],
  },
];

export const Students = () => {
  const { classId } = useParams();
  const location = useLocation();
  const className = location.state?.className || null;
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();
  const { school } = useContext(AuthContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [students, setStudents] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [previousPage, setPreviousPage] = useState(null);
  const PAGE_SIZE = 10; 

  // For QR code modal
  const [showQRModal, setShowQRModal] = useState(false);
  const handleOpenQRModal = () => setShowQRModal(true);
  const handleCloseQRModal = () => setShowQRModal(false);
  const qrUrl = API_URLS.student_attendance_check();
  const qrTitle = t("admin_pages.students.qr_student_attendance_title");
  const qrDescription = t("admin_pages.students.qr_student_attendance_description");

  const filters = useMemo(() => SEARCH_FILTERS(t), [t]);

  const fetchRooms = useCallback(async () => {
  try {
    const response = await axiosPrivate.get(API_URLS.getRooms(auth.id, school.id));
    setRooms(response.data?.results.map((room) => ({ value: room.name, label: room.name })) ?? []);
  } catch (err) {
    console.error("Rooms fetch error:", err);
  }
}, [auth.id, axiosPrivate, school.id]);

useEffect(() => {
  fetchRooms();
}, [fetchRooms]);


const fetchStudents = useCallback(async (pageNumber = 1) => {
    setLoading(true);
    setError(null);
    try {
        let baseUrl;
        baseUrl = API_URLS.getStudents(auth.id, school.id);

        const params = new URLSearchParams(searchParams);
        params.set("page", pageNumber);
        params.set("page_size", PAGE_SIZE);

        const finalUrl = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${params.toString()}`;

        const response = await axiosPrivate.get(finalUrl);
        const { results, total_items, total_pages, links } = response.data;

        const formatted = formatStudentData(results);
        setStudents(formatted);
        setFilteredStudents(formatted);

        setTotalCount(total_items);
        setTotalPages(total_pages);
        setNextPage(links?.next);
        setPreviousPage(links?.previous);
        setCurrentPage(pageNumber);

    } catch (err) {
        console.error("GET error:", err);
        toast.error(t("students.loadError"));
        setError(t("students.loadError"));
    } finally {
        setLoading(false);
    }
}, [classId, auth.id, axiosPrivate, school.id, t, searchParams]);

  const getSelectedOption = (key, options) => {
    const value = searchParams.get(key)?? (key === "status" ? "active" : null);;
    return options.find((o) => o.value === value) ?? null;
  };

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (!params.has("status")) {
      params.set("status", "active");
      setSearchParams(params, { replace: true });
    }
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
    fetchStudents();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [fetchStudents]);

  const handleSearch = (value, filter) => {
    const params = new URLSearchParams(searchParams);

    if (value && filter) 
      params.set(filter, value);
    else
       filters.forEach(f => params.delete(f.value));

    setSearchParams(params);

  };

  const handleExport = () => {
    const headers = COLUMNS.filter((col) => col.key !== "actions").map(
      (col) => col.label
    );
    const data = students.map((student) => [
      student.name,
      student.age,
      student.rooms,
      student.parents,
      student.status,
      student.phone,
    ]);
    downloadCSV(data, headers, "students");
  };

  const handleDelete = async (row) => {
    if (!row) return;

    try {
      setLoading(true);
      await axiosPrivate.delete(API_URLS.deleteStudent(row.id, school.id));
      toast.success("Student deleted successfully.");
      fetchStudents();
    } catch (err) {
      console.error("Delete error:", err);
      toast.error(t("students.deleteError"));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (row, isFeed) => {
    navigate(`${ADMIN_STUDENTS_ROUTE}/details/${row.id}`, {
      state: { isFeed },
    });
  };


  const handleFilter = (key, value) => {
   const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value); 
    } else {
      params.delete(key);   
    }
    setSearchParams(params); 
  };

  const handleSort = (key, direction) => {
    const params = new URLSearchParams(searchParams);
    if (key && direction) {
      params.set("ordering", direction === "asc" ? key : `-${key}`);
    } else {
      params.delete("ordering");
    }
    setSearchParams(params, { replace: true });
  };

const studentFilters = useMemo(
  () => getStudentFilters(t, rooms),
  [t, rooms]
);

  return (
    <div className="d-block ms-auto px-4" style={{ minHeight: "70vh" }}>
      <HeaderComponent
        buttonName="add_student"
        export_fn={handleExport}
        setShow={() => setShowAddModal(true)}
        title="students_of_class"
        className={className}
        setGenerate={handleOpenQRModal}
      />

      <SearchComponent
        items={filters}
        onSearch={handleSearch}
        placeholder={"student"}
      />
      <div className="d-flex gap-2 mb-3">
        {studentFilters.map(({ key, options }) => (
          <GeneralDropdown
            key={key}
            noBorder
            isClearable
            options={options}
            placeholder={t(`students.filters.${key}`)}
            onChange={(e) => handleFilter(key, e?.value ?? "")}
            selectedOption={getSelectedOption(key, options)}
          />
        ))}
      </div>

      {loading ? (
        <LoadingDots />
      ) : error ? (
        <div className="d-flex justify-content-center align-items-center vh-100 fw-bold fs-1 text-danger">
          {error}
        </div>
      ) :  (
        <>
        <TableComponent
          columns={COLUMNS}
          data={filteredStudents}
          rooms={rooms}
          page="student"
          statusOptions={STATUS_OPTIONS(t)} 
          onFilterChange={handleFilter} 
          onDelete={handleDelete}
          onEdit={handleEdit}
          onSort={handleSort}
          getRowUrl={(row) => `${ADMIN_STUDENTS_ROUTE}/details/${row.id}`}
        />
        {filteredStudents.length === 0 && (
          <div className="d-flex justify-content-center align-items-center fw-bold fs-1">
            {t("students.noData")}
          </div>
        )}
        <Pagination 
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          nextPage={nextPage}
          previousPage={previousPage}
          onPageChange={(p) => fetchStudents(p)}
          pageSize={PAGE_SIZE} 
        />
        </>
      )}

      <AddstudentModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="student"
      />

      <QRModal title={qrTitle} description={qrDescription} qrUrl={qrUrl} open={showQRModal} onClose={handleCloseQRModal}/>
    </div>
  );
};